import { useState, useCallback, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Button, Badge } from '@/components/v2/ui';
import {
  convertImages,
  formatFileSize,
  FORMAT_INFO,
  FORMATS_WITH_QUALITY,
  type ImageFormat,
} from '@/lib/api/conversion';
import { type ImageOptimizeState } from './useImageOptimizeState';
import { cn } from '@/lib/utils';

interface Props {
  state: ImageOptimizeState;
  setState: Dispatch<SetStateAction<ImageOptimizeState>>;
}

const FORMATS: ImageFormat[] = ['png', 'jpeg', 'webp', 'avif'];

interface ConvertResult {
  originalSize: number;
  convertedSize: number;
  width: number;
  height: number;
}

export function FormatConvertSection({ state, setState }: Props) {
  const { t } = useTranslation();
  const [format, setFormat] = useState<ImageFormat>('webp');
  const [quality, setQuality] = useState(90);
  const [isConverting, setIsConverting] = useState(false);
  const [result, setResult] = useState<ConvertResult | null>(null);

  const handleConvert = useCallback(async () => {
    if (!state.currentFile) return;
    setIsConverting(true);
    setState(prev => ({ ...prev, isEditing: true }));
    try {
      const response = await convertImages(
        [state.currentFile],
        format,
        FORMATS_WITH_QUALITY.includes(format) ? quality : undefined
      );

      const item = response.images?.[0] ?? response;
      if (!item.success) throw new Error(item.error || 'Failed');

      // Build blob URL from base64 for preview
      const base64 = (item as { image?: string }).image;
      let imageUrl = '';
      if (base64) {
        const mime = `image/${format}`;
        const bin = atob(base64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const blob = new Blob([bytes], { type: mime });
        imageUrl = URL.createObjectURL(blob);
      }

      setResult({
        originalSize: item.original_size ?? state.currentFile.size,
        convertedSize: item.converted_size ?? 0,
        width: 0,
        height: 0,
      });

      setState(prev => ({
        ...prev,
        isEditing: false,
        editedImageUrl: imageUrl || prev.editedImageUrl,
        editHistory: [...prev.editHistory, {
          timestamp: new Date(),
          action: 'convert',
          labelKey: 'imageOptimize.layout.historyConvert',
          imageUrl: imageUrl || prev.editedImageUrl || '',
          s3Key: (item as { s3_key?: string }).s3_key ?? '',
        }],
      }));
    } catch {
      setState(prev => ({ ...prev, isEditing: false }));
    } finally {
      setIsConverting(false);
    }
  }, [state.currentFile, format, quality, setState]);

  const hasQuality = FORMATS_WITH_QUALITY.includes(format);
  const compressionPct = result
    ? ((1 - result.convertedSize / result.originalSize) * 100)
    : null;
  const isSmaller = result ? result.convertedSize < result.originalSize : false;

  return (
    <div className="space-y-3">
      {/* Format Grid */}
      <div>
        <p className="text-sm font-medium mb-2">{t('imageOptimize.layout.format')}</p>
        <div className="grid grid-cols-2 gap-2">
          {FORMATS.map(f => (
            <button
              key={f}
              onClick={() => { setFormat(f); setResult(null); }}
              disabled={isConverting}
              className={cn(
                'p-2.5 rounded-lg border text-left transition-colors',
                format === f
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300'
              )}
            >
              <p className="text-xs font-bold uppercase tracking-wider">{FORMAT_INFO[f].name}</p>
              <p className="text-2xs text-neutral-500 dark:text-neutral-400">{FORMAT_INFO[f].info}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Quality Slider */}
      {hasQuality && (
        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium">{t('imageOptimize.layout.quality')}</label>
            <span className="text-sm font-bold text-primary-600 dark:text-primary-400">{quality}%</span>
          </div>
          <input
            type="range" min="1" max="100" value={quality}
            onChange={e => { setQuality(Number(e.target.value)); setResult(null); }}
            disabled={isConverting}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-neutral-200 dark:bg-neutral-700 accent-primary-500"
          />
          <p className="text-2xs text-neutral-500 text-center">{t('imageOptimize.layout.qualityHint')}</p>
        </div>
      )}

      {/* Convert Button */}
      <Button variant="secondary" className="w-full" disabled={!state.currentFile || isConverting}
        loading={isConverting} onClick={handleConvert}>
        {t('imageOptimize.layout.convertTo', { format: FORMAT_INFO[format].name })}
      </Button>

      {/* Size Comparison */}
      {result && (
        <div className="p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
            <div className="bg-neutral-100 dark:bg-neutral-800 rounded-md p-3 space-y-1">
              <Badge variant="secondary" size="sm">Original</Badge>
              <p className="text-lg font-bold">{formatFileSize(result.originalSize)}</p>
            </div>
            <ArrowRight className="h-4 w-4 text-neutral-400" />
            <div className={cn(
              'rounded-md p-3 space-y-1',
              isSmaller ? 'bg-green-50 dark:bg-green-950/20' : 'bg-orange-50 dark:bg-orange-950/20'
            )}>
              <Badge variant={isSmaller ? 'success' : 'warning'} size="sm">Converted</Badge>
              <p className="text-lg font-bold">{formatFileSize(result.convertedSize)}</p>
            </div>
          </div>
          {compressionPct !== null && (
            <div className="mt-2 pt-2 border-t border-neutral-200 dark:border-neutral-800 text-center">
              <span className={cn(
                'text-sm font-bold',
                isSmaller ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'
              )}>
                {isSmaller ? '↓' : '↑'} {Math.abs(compressionPct).toFixed(1)}%{' '}
                {isSmaller ? t('converter.smaller') : t('converter.larger')}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
